import {
  Box,
  Divider,
  HStack,
  Icon,
  Spinner,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { FiInfo, FiTrendingDown, FiTrendingUp } from "react-icons/fi";
import type { CampaignIntent } from "common/interfaces/KYDMarketing";
import type { InsightItem } from "../adSetCardUtils";
import { getInsightVariantColors } from "../adSetCardUtils";
import {
  getAdInsightPrimaryAction,
  type AdInsightPrimaryAction,
} from "./adInsightActions";
import { CampaignDetailLink } from "./CampaignDetailLink";

function PrimaryActionLink({
  action,
  intent,
  applyingIntentId,
  onOpenWizardReview,
  onApplyIntent,
  isApplyingChanges,
}: {
  action: AdInsightPrimaryAction;
  intent: CampaignIntent;
  applyingIntentId: string | null | undefined;
  onOpenWizardReview?: () => void;
  onApplyIntent?: (intent: CampaignIntent) => void | Promise<void>;
  isApplyingChanges?: boolean;
}) {
  if (action.type === "none") return null;

  if (action.type === "openReview") {
    return (
      <CampaignDetailLink
        onClick={
          isApplyingChanges
            ? undefined
            : (e) => {
                e.stopPropagation();
                onOpenWizardReview?.();
              }
        }
        aria-disabled={isApplyingChanges}
        _disabled={{
          color: "gray.400",
          textDecoration: "none",
          cursor: "not-allowed",
        }}
      >
        {action.label}
      </CampaignDetailLink>
    );
  }

  return (
    <CampaignDetailLink
      onClick={
        isApplyingChanges
          ? undefined
          : (e) => {
              e.stopPropagation();
              if (applyingIntentId !== intent.id) {
                onApplyIntent?.(intent);
              }
            }
      }
      opacity={applyingIntentId === intent.id ? 0.8 : 1}
      cursor={applyingIntentId === intent.id ? "wait" : undefined}
      pointerEvents={applyingIntentId === intent.id ? "none" : undefined}
      aria-disabled={isApplyingChanges}
      _disabled={{
        color: "gray.400",
        textDecoration: "none",
        cursor: "not-allowed",
      }}
    >
      <HStack gap={1.5} display="inline-flex">
        {applyingIntentId === intent.id && <Spinner size="xs" />}
        <span>
          {applyingIntentId === intent.id ? "Applying…" : action.label}
        </span>
      </HStack>
    </CampaignDetailLink>
  );
}

export function AdInsightRow({
  item,
  showDivider,
  onOpenWizardReview,
  onApplyIntent,
  onIgnoreIntent,
  applyingIntentId,
  ignoringIntentId,
  isApplyingChanges = false,
}: {
  item: InsightItem;
  showDivider: boolean;
  onOpenWizardReview?: () => void;
  onApplyIntent?: (intent: CampaignIntent) => void | Promise<void>;
  onIgnoreIntent?: (intent: CampaignIntent) => void | Promise<void>;
  applyingIntentId?: string | null;
  ignoringIntentId?: string | null;
  isApplyingChanges?: boolean;
}) {
  const colors = getInsightVariantColors(item.variant);
  const primaryAction = getAdInsightPrimaryAction(item);
  const hasPrimaryAction = primaryAction.type !== "none";

  return (
    <Box>
      {showDivider && (
        <Divider borderColor="gray.200" mb={2} borderBottomWidth="1px" />
      )}
      <HStack spacing={3} align="center" w="100%">
        <Box
          borderRadius="8px"
          bg={colors.bg}
          p={1.5}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Icon
            as={
              item.variant === "positive"
                ? FiTrendingUp
                : item.variant === "negative"
                  ? FiTrendingDown
                  : FiInfo
            }
            color={colors.color}
            boxSize={5}
          />
        </Box>
        <Tooltip
          label={item.fullExplanation}
          hasArrow
          placement="top"
          maxW="360px"
          whiteSpace="pre-wrap"
          isDisabled={!item.fullExplanation}
        >
          <Text
            fontSize="sm"
            fontWeight={600}
            color="gray.600"
            flex={1}
            lineHeight="130%"
            noOfLines={1}
            cursor={item.fullExplanation ? "help" : undefined}
          >
            {item.line}
          </Text>
        </Tooltip>
        <HStack gap={1} align="center" flexShrink={0}>
          <PrimaryActionLink
            action={primaryAction}
            intent={item.intent}
            applyingIntentId={applyingIntentId}
            onOpenWizardReview={onOpenWizardReview}
            onApplyIntent={onApplyIntent}
            isApplyingChanges={isApplyingChanges}
          />
          {hasPrimaryAction && (
            <Text color="gray.400" fontWeight="bold">
              •
            </Text>
          )}
          <CampaignDetailLink
            onClick={
              isApplyingChanges
                ? undefined
                : (e) => {
                    e.stopPropagation();
                    if (ignoringIntentId !== item.intent.id) {
                      onIgnoreIntent?.(item.intent);
                    }
                  }
            }
            opacity={ignoringIntentId === item.intent.id ? 0.8 : 1}
            cursor={ignoringIntentId === item.intent.id ? "wait" : undefined}
            pointerEvents={
              ignoringIntentId === item.intent.id ? "none" : undefined
            }
            aria-disabled={
              isApplyingChanges || ignoringIntentId === item.intent.id
            }
            _disabled={{
              color: "gray.400",
              textDecoration: "none",
              cursor: "not-allowed",
            }}
          >
            <HStack gap={1.5} display="inline-flex">
              {ignoringIntentId === item.intent.id && <Spinner size="xs" />}
              <span>
                {ignoringIntentId === item.intent.id ? "Ignoring…" : "Ignore"}
              </span>
            </HStack>
          </CampaignDetailLink>
        </HStack>
      </HStack>
    </Box>
  );
}
